// A ROM picked in the library rides to /play, where the core exists to
// import it. The player is always a full page load (only /play is served
// cross-origin isolated, see openPlayer), so the file waits in IndexedDB
// (as bytes: WebKit refuses some Blobs there) and its name rides in the
// URL as `pending`.

const DB = "pocket-play-pending";
const STORE = "rom";
const KEY = "rom";

interface Stashed {
  name: string;
  type: string;
  bytes: ArrayBuffer;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/** The player's address, for a link or a full page load. */
export function playUrl(params: Record<string, string> = {}): string {
  const query = new URLSearchParams(params).toString();
  return query ? `/play?${query}` : "/play";
}

/** Opens the player in a full page load, so it is served cross-origin isolated. */
export function openPlayer(url = playUrl()) {
  window.location.assign(url);
}

/** Keeps `file` for the player and opens it. */
export async function playFile(file: File) {
  const stashed: Stashed = { name: file.name, type: file.type, bytes: await file.arrayBuffer() };
  await run("readwrite", (store) => store.put(stashed, KEY));
  openPlayer(playUrl({ pending: file.name }));
}

/** The name of the file waiting for the player, from the URL. */
export function pendingName(): string | null {
  return new URLSearchParams(window.location.search).get("pending");
}

/** The file waiting for the player, once; null when there is none. */
export async function takeRom(): Promise<File | null> {
  const name = pendingName();
  if (!name) return null;
  const stashed = await run<Stashed | undefined>("readonly", (store) => store.get(KEY)).catch(() => undefined);
  await run("readwrite", (store) => store.delete(KEY)).catch(() => {});
  if (!stashed || stashed.name !== name) return null;
  return new File([stashed.bytes], stashed.name, { type: stashed.type });
}
